import { useCallback, useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import {
  Alert,
  Box,
  Button,
  Flex,
  Link,
  SearchField,
  Skeleton,
  Text,
} from '@backstage/ui';

export interface OrgRepo {
  name: string;
  fullName: string;
  description?: string;
  language?: string;
  defaultBranch: string;
  htmlUrl: string;
  pushedAt?: string;
}

interface ReadinessCheck {
  path: string;
  present: boolean;
  why: string;
  severity: 'blocking' | 'advised';
  /** `portal` — the golden path's PR adds it. `control` — gap-filled after approval. */
  addedBy: 'portal' | 'control';
}

export interface RepoReadiness {
  repo: string;
  defaultBranch: string;
  checks: ReadinessCheck[];
  present: string[];
  /**
   * The repository's OWN catalog-info.yaml, run through the catalog's
   * validation. The golden path keeps that file as it is and registers it, so
   * an invalid one would only fail at the very end — after the pull request.
   * Absent when there is none or the check could not run.
   */
  catalogInfo?: { valid: boolean; problems: string[]; message?: string };
}

/**
 * `Suntory-Toluna-ETL` -> `suntory-toluna-etl`.
 *
 * Matches `serviceSpecSchema.name`: /^[a-z][a-z0-9-]*$/, min 2. A leading
 * non-letter is dropped rather than substituted — `2fa-service` becoming
 * `x2fa-service` would be a name nobody chose.
 */
export function suggestServiceName(repo: string): string {
  return repo
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/, '');
}

/** What the field emits: the repository, plus what it is missing. */
export interface RepoSelection {
  name: string;
  defaultBranch: string;
  htmlUrl: string;
  missing: string[];
  /**
   * Skeleton files the repository already has.
   *
   * The template deletes these from its workspace before opening the pull
   * request. Without it, `publish:github:pull-request` commits the whole
   * rendered workspace — including a fresh `.github/workflows/ci.yml` over the
   * top of the repository's own — while the PR body says it "only ADDS".
   */
  present: string[];
  /**
   * A service name derived from the repository, for the next step to offer.
   *
   * Normalised to what Control's spec accepts — lowercase, hyphens, starts
   * with a letter — because `Suntory-Toluna-ETL` is a perfectly good repository
   * name and not a legal service name, and finding that out at the end of the
   * form is worse than being handed the answer at the start.
   */
  suggestedName: string;
  /** Environments this repository is ALREADY running in, if any. */
  deployedTo: string[];
}

/**
 * Pick a repository that already exists in the org.
 *
 * # Why this replaced a text box
 *
 * The field it replaces accepted free text. That is how
 * `https://github.com/answerrocket/foo` was submitted as a repository NAME,
 * accepted, and died forty seconds later inside Control's preflight with an
 * error about token permissions — pointing at the credential rather than at the
 * input. Control's `service-spec.ts` still carries the normaliser written to
 * survive that, and it should stay; this just means it is a second line of
 * defence rather than the only one.
 *
 * A picker also cannot produce a repository that does not exist, is archived,
 * or belongs to another organisation, which between them are most of the other
 * ways the field was got wrong.
 *
 * # Why it shows what the repository is missing
 *
 * Because the answer is almost always "some of it", and because the wizard can
 * fix that. Every service on the paved road needs a Dockerfile, a
 * cloudbuild.yaml and a catalog-info.yaml, and most repositories somebody built
 * over a weekend have none of the three. Showing the gap here — before any
 * infrastructure is requested — is the difference between "the portal adds
 * these three files for you" and a deploy that fails on its first build for a
 * reason nobody explained.
 */
export const OrgRepoPicker = (
  props: FieldExtensionComponentProps<RepoSelection>,
) => {
  const {
    onChange,
    schema: { title, description },
  } = props;

  // `required`/`rawErrors` are not read: the field satisfies the form only once
  // a repository is chosen, so an empty one blocks Next without an error
  // message. `formData` is not read either — a remembered choice is re-checked
  // rather than restored, because what a repository is MISSING can change
  // between visits and a stale readiness list drives the fixing pull request.
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [query, setQuery] = useState('');
  const [repos, setRepos] = useState<OrgRepo[]>([]);
  const [selected, setSelected] = useState<OrgRepo | null>(null);
  const [readiness, setReadiness] = useState<RepoReadiness>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  /**
   * repo name -> the environments it is already running in.
   *
   * Read from the CATALOG rather than from a new upstream call, because the
   * catalog already knows: `modules/launchpad/DeploymentProcessor` attaches
   * `answerrocket.com/deployed-to` to whatever entity a repository owns, from
   * launchpad's inventory (which derives from terraform). So this is an
   * OBSERVED fact — what is actually running — not a claim somebody typed.
   */
  const [deployments, setDeployments] = useState<Record<string, string[]>>({});

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const search = useCallback(
    async (q: string) => {
      setLoading(true);
      setError(undefined);
      try {
        const baseUrl = await discovery.getBaseUrl('github');
        const response = await fetchApi.fetch(
          `${baseUrl}/repos${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        );
        if (!response.ok) {
          throw new Error(`${response.status} ${await response.text()}`);
        }
        const body = (await response.json()) as { repos: OrgRepo[] };
        setRepos(body.repos);

        // Which of these are already running somewhere. One catalog query for
        // the whole page rather than one per row, and failures are swallowed:
        // the badge is useful context, not a gate, and a catalog hiccup must
        // not stop somebody picking a repository.
        try {
          const slugs = body.repos.map(
            r => `github.com/project-slug=answerrocket/${r.name}`,
          );
          if (slugs.length) {
            const { items } = await catalogApi.getEntities({
              filter: slugs.map(slug => {
                const [key, value] = slug.split('=');
                return {
                  kind: 'Component',
                  [`metadata.annotations.${key}`]: value,
                };
              }),
              fields: ['metadata.name', 'metadata.annotations'],
            });
            const found: Record<string, string[]> = {};
            for (const item of items) {
              const ann = item.metadata.annotations ?? {};
              const slug = ann['github.com/project-slug'];
              const envs = ann['answerrocket.com/deployed-to'];
              if (!slug || !envs) continue;
              found[slug.split('/').pop()!] = envs
                .split(',')
                .map(v => v.trim())
                .filter(Boolean);
            }
            setDeployments(found);
          }
        } catch {
          // Context, not a gate.
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [catalogApi, discovery, fetchApi],
  );

  useEffect(() => {
    // Debounced: GitHub's search API is rate limited per installation, and a
    // keystroke-per-request picker exhausts it for everyone at once.
    const timer = setTimeout(() => void search(query), query ? 300 : 0);
    return () => clearTimeout(timer);
  }, [query, search]);

  const choose = useCallback(
    async (repo: OrgRepo | null) => {
      setSelected(repo);
      setReadiness(undefined);
      if (!repo) {
        onChangeRef.current(undefined as unknown as RepoSelection);
        return;
      }
      // Satisfy the field immediately with what we know; refine when the
      // readiness read lands. Otherwise a slow tree read leaves Next disabled
      // on a repository the user has visibly chosen.
      onChangeRef.current({
        name: repo.name,
        defaultBranch: repo.defaultBranch,
        htmlUrl: repo.htmlUrl,
        // Both empty until the readiness read lands, and they stay empty if it
        // fails. That is safe: the template opens its pull request only when
        // `missing` is non-empty, so no read means no pull request, and nothing
        // can be committed over files the repository already has.
        missing: [],
        present: [],
        suggestedName: suggestServiceName(repo.name),
        deployedTo: deployments[repo.name] ?? [],
      });
      try {
        const baseUrl = await discovery.getBaseUrl('github');
        const response = await fetchApi.fetch(
          `${baseUrl}/repos/${encodeURIComponent(repo.name)}/readiness`,
        );
        if (!response.ok) throw new Error(`${response.status}`);
        const result = (await response.json()) as RepoReadiness;
        setReadiness(result);
        // A descriptor the catalog would refuse blocks Next: un-satisfying the
        // field is how this form blocks (see `required` above), and every step
        // after this one would be wasted — the task refuses it anyway, at
        // github:catalog-info:preflight.
        if (result.catalogInfo && !result.catalogInfo.valid) {
          onChangeRef.current(undefined as unknown as RepoSelection);
          return;
        }
        onChangeRef.current({
          name: repo.name,
          defaultBranch: result.defaultBranch,
          htmlUrl: repo.htmlUrl,
          // Only what the pull request ADDS: blocking files the portal
          // supplies. Advised ones are suggestions in its body, never commits.
          missing: result.checks
            .filter(
              c =>
                !c.present &&
                c.addedBy === 'portal' &&
                c.severity === 'blocking',
            )
            .map(c => c.path),
          present: result.present,
          suggestedName: suggestServiceName(repo.name),
          deployedTo: deployments[repo.name] ?? [],
        });
      } catch {
        // A readiness read that fails must not block the wizard. `missing`
        // stays empty, so the template opens no pull request at all: the worst
        // case is a missing catalog-info.yaml, which the requester finds out
        // from the entity page rather than from an overwritten file.
        setReadiness(undefined);
      }
    },
    [deployments, discovery, fetchApi],
  );

  const brokenCatalogInfo =
    readiness?.catalogInfo && !readiness.catalogInfo.valid
      ? readiness.catalogInfo
      : undefined;
  const missing = readiness?.checks.filter(c => !c.present) ?? [];
  const portalAdds = missing.filter(
    c => c.addedBy === 'portal' && c.severity === 'blocking',
  );
  const suggested = missing.filter(c => c.severity === 'advised');
  const controlAdds = missing.filter(c => c.addedBy === 'control');

  /** Present / missing-and-blocking / missing-but-fine, as three colours.
   *
   *  A missing `mkdocs.yml` and a missing `Dockerfile` are not the same news,
   *  and drawing both as one red mark teaches people to ignore the red marks. */
  const dotColour = (check: ReadinessCheck) => {
    if (check.path === 'catalog-info.yaml' && brokenCatalogInfo) {
      return 'var(--ar-warn)';
    }
    if (check.present) return 'var(--ar-ok)';
    if (check.severity === 'blocking') return 'var(--ar-warn)';
    return 'var(--ar-idle)';
  };

  return (
    <Box>
      <Text
        as="p"
        variant="body-small"
        color="secondary"
        style={{ marginBottom: 10 }}
      >
        {description ??
          'Pick the repository you want to deploy. It has to be in the answerrocket organisation.'}
      </Text>

      {error && (
        <Alert
          status="warning"
          title="Could not read the organisation"
          description={error}
        />
      )}

      {/* A search field, not a combobox with free text. `freeSolo` is what
          would let a pasted browser URL back in, and that is exactly how a URL
          was once submitted as a repository NAME. */}
      <SearchField
        label={title ?? 'Repository'}
        placeholder="Start typing a repository name"
        value={query}
        onChange={setQuery}
      />

      {loading && (
        <Box mt="2">
          <Skeleton style={{ height: 40 }} />
        </Box>
      )}

      {!loading && !selected && repos.length > 0 && (
        <Box
          mt="2"
          style={{
            border: '1px solid var(--bui-border-1)',
            borderRadius: 'var(--bui-radius-2)',
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          {/* Plain buttons, not BUI Button.
           *
           * BUI's Button centres its content and sizes to it — a row built
           * from one rendered as a centred column in the middle of a
           * full-width strip, with the next row's text colliding into it. It
           * is the right component for an action and the wrong one for a list
           * row, and no amount of justifyContent argues it out of its own
           * layout.
           *
           * A button element with our own grid is what a row actually is:
           * left-aligned, fixed columns, the whole row a hit target. */}
          {repos.map((repo, i) => {
            const deployed = deployments[repo.name];
            return (
              <button
                type="button"
                key={repo.name}
                onClick={() => void choose(repo)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0,1fr) auto',
                  gap: 12,
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                  padding: '11px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderTop: i === 0 ? 'none' : '1px solid var(--bui-border-2)',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background =
                    'var(--bui-bg-neutral-1-hover)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ minWidth: 0 }}>
                  {/* The NAME in the body face, not mono.
                   *
                   * Mono earns its place where characters are being compared
                   * or copied — a project id, a run id, a file path. A
                   * repository name in a list is being SCANNED, as a word,
                   * and monospace makes a column of words wider, lighter and
                   * harder to read at a glance. `Suntory-Toluna-ETL` in mono
                   * reads as a serial number. */}
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      letterSpacing: '-0.006em',
                      lineHeight: 1.35,
                      color: 'var(--bui-fg-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {repo.name}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.8125rem',
                      lineHeight: 1.45,
                      color: 'var(--bui-fg-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {[repo.language, repo.description]
                      .filter(Boolean)
                      .join(' · ') || 'No description'}
                  </span>
                </span>
                {/* Already deployed somewhere? Read from the catalog's own
                 * observed annotations, so it reflects what is actually
                 * running rather than what somebody wrote down. Deploying a
                 * second copy of something already live is the mistake worth
                 * catching before the form, not after the pull request. */}
                {deployed && deployed.length > 0 && (
                  <span
                    style={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      whiteSpace: 'nowrap',
                      color: 'var(--ar-warn)',
                      border: '1px solid var(--ar-warn)',
                      borderRadius: 999,
                      padding: '2px 9px',
                    }}
                  >
                    Live in {deployed.length}{' '}
                    {deployed.length === 1 ? 'environment' : 'environments'}
                  </span>
                )}
              </button>
            );
          })}
        </Box>
      )}

      {!loading && !selected && repos.length === 0 && query.trim() && (
        <Box mt="2">
          <Text variant="body-small" color="secondary">
            Nothing in the answerrocket organisation matches “{query.trim()}”.
          </Text>
        </Box>
      )}

      {selected && !readiness && (
        <Box mt="3">
          <Text as="p" variant="body-small" color="secondary">
            Checking what {selected.name} already has…
          </Text>
          <Skeleton style={{ height: 120, marginTop: 8 }} />
        </Box>
      )}

      {/* Already running somewhere. Not a block — redeploying an existing
          service into a second environment is legitimate — but it is the one
          thing worth interrupting for, because the OTHER reading is "somebody
          is about to create a duplicate of a service that already exists". */}
      {selected && (deployments[selected.name]?.length ?? 0) > 0 && (
        <Box mt="3">
          <Alert
            status="info"
            title={`${selected.name} is already deployed`}
            description={`It is running in ${deployments[selected.name].join(
              ', ',
            )}. Carry on if you are adding another environment — but if you are recreating something that already exists, stop here and deploy to the existing service instead.`}
          />
        </Box>
      )}

      {readiness && (
        <Box mt="4">
          <Flex align="center" gap="2" style={{ marginBottom: 10 }}>
            <Text weight="bold" style={{ fontSize: '0.9375rem' }}>
              What {readiness.repo} already has
            </Text>
            {/* A styled span, not BUI's Tag: Tag is a react-aria COLLECTION item and
                throws "cannot be rendered outside a collection" on its own,
                which took the whole readiness panel down. It is also the wrong
                component — a branch name here is a fact, not a removable
                chip. */}
            <span
              style={{
                fontFamily: 'var(--ar-font-mono)',
                fontSize: '0.75rem',
                color: 'var(--bui-fg-secondary)',
                border: '1px solid var(--bui-border-1)',
                borderRadius: 'var(--bui-radius-2)',
                padding: '1px 6px',
              }}
            >
              {readiness.defaultBranch}
            </span>
            <Button
              size="small"
              variant="tertiary"
              onPress={() => void choose(null)}
            >
              Change
            </Button>
          </Flex>

          {brokenCatalogInfo && (
            <Box mb="3">
              <Alert
                status="danger"
                title="Fix this repository's catalog-info.yaml first"
                description={
                  brokenCatalogInfo.message ??
                  `Your repository's catalog-info.yaml is invalid: ${brokenCatalogInfo.problems.join(
                    '; ',
                  )}.`
                }
              />
            </Box>
          )}

          {readiness.checks.map(check => (
            <Flex
              key={check.path}
              gap="3"
              align="start"
              style={{ padding: '5px 0' }}
            >
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  marginTop: 7,
                  flexShrink: 0,
                  background: dotColour(check),
                }}
              />
              <Box>
                <Text
                  variant="body-small"
                  style={{ fontFamily: 'var(--ar-font-mono)' }}
                >
                  {check.path}
                </Text>
                {!check.present && (
                  <Text as="p" variant="body-small" color="secondary">
                    {check.why}
                  </Text>
                )}
                {check.path === 'catalog-info.yaml' && brokenCatalogInfo && (
                  <Text as="p" variant="body-small" color="secondary">
                    Present, but the catalog would refuse it:{' '}
                    {brokenCatalogInfo.problems.join('; ')}.
                  </Text>
                )}
              </Box>
            </Flex>
          ))}

          <Box
            mt="3"
            style={{
              border: '1px solid var(--bui-border-1)',
              borderRadius: 'var(--bui-radius-2)',
              padding: '10px 12px',
            }}
          >
            {missing.length === 0 && !brokenCatalogInfo && (
              <Text as="p" variant="body-small">
                Nothing missing —{' '}
                <Link href={selected!.htmlUrl}>{readiness.repo}</Link> is
                already set up the way the paved road expects.
              </Text>
            )}
            {portalAdds.length > 0 && (
              <Text as="p" variant="body-small">
                The portal will open <strong>one pull request</strong> on{' '}
                <Link href={selected!.htmlUrl}>{readiness.repo}</Link> adding{' '}
                {portalAdds.map(c => c.path).join(', ')}. It only ADDS — files
                you already have are left exactly as they are, and your team
                reviews it like any other pull request.
              </Text>
            )}
            {suggested.length > 0 && portalAdds.length > 0 && (
              <Text
                as="p"
                variant="body-small"
                color="secondary"
                style={{ marginTop: portalAdds.length > 0 ? 8 : 0 }}
              >
                {suggested.map(c => c.path).join(', ')}{' '}
                {suggested.length === 1 ? 'is' : 'are'} optional and{' '}
                <strong>not added</strong> — the pull request lists{' '}
                {suggested.length === 1 ? 'it' : 'them'} as suggestions your
                team can take or leave.
              </Text>
            )}
            {controlAdds.length > 0 && (
              <Text
                as="p"
                variant="body-small"
                color="secondary"
                style={{
                  marginTop: portalAdds.length + suggested.length > 0 ? 8 : 0,
                }}
              >
                {controlAdds.map(c => c.path).join(' and ')}{' '}
                {controlAdds.length === 1 ? 'is' : 'are'} added by Control after
                approval, not by this form — a build file for an app somebody
                has already written has to match what the app actually is, and a
                guessed one does not build.
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
